import React, { useEffect, useRef, useState } from 'react'

// 상세 내용용 간단 서식 편집기 — 전자결재(Streamlit)의 Quill과 같은 범위의 HTML만 만든다.
// 저장 시 서버(edoc_api.clean_rich_html)가 허용 태그만 남기고 정리한다.
const TOOLS = [
  { cmd: 'bold', label: 'B', title: '굵게', style: { fontWeight: 700 } },
  { cmd: 'italic', label: 'I', title: '기울임', style: { fontStyle: 'italic' } },
  { cmd: 'underline', label: 'U', title: '밑줄', style: { textDecoration: 'underline' } },
  { cmd: 'strikeThrough', label: 'S', title: '취소선', style: { textDecoration: 'line-through' } },
  { sep: true },
  { cmd: 'formatBlock', arg: '<h3>', label: 'H', title: '제목' },
  { cmd: 'insertOrderedList', label: '1.', title: '번호 목록' },
  { cmd: 'insertUnorderedList', label: '•', title: '글머리 목록' },
  { cmd: 'formatBlock', arg: '<blockquote>', label: '❝', title: '인용' },
  { sep: true },
  { cmd: 'link', label: '🔗', title: '링크' },
  { cmd: 'removeFormat', label: 'Tx', title: '서식 지우기' },
]

// 붙여넣은 캡처는 원본 그대로 두면 수 MB짜리 base64가 되니 줄여서 넣는다.
// 글자가 많은 캡처는 PNG가 선명해서 우선 PNG로 두되, 너무 크면 JPEG로 떨어뜨린다.
async function shrinkImage(file, maxSide = 1400, pngLimit = 700 * 1024) {
  const bitmap = await createImageBitmap(file)
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height))
  const w = Math.round(bitmap.width * scale)
  const h = Math.round(bitmap.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h)
  bitmap.close?.()
  const png = canvas.toDataURL('image/png')
  return png.length <= pngLimit ? png : canvas.toDataURL('image/jpeg', 0.85)
}

// uploadImage(dataUrl) -> src 문자열. 주면 캡처를 서버에 올리고 주소만 본문에 남긴다.
// 안 주면 예전대로 base64를 그대로 넣는다 - 전자결재는 문서마다 캡처가 한두 장이라
// 그쪽을 건드릴 이유가 없다. 매뉴얼은 한 문서에 캡처가 수십 장이라 사정이 다르다.
export default function RichTextEditor({ value = '', onChange, placeholder = '', minHeight = 180, uploadImage }) {
  const ref = useRef(null)
  const [empty, setEmpty] = useState(true)

  // value는 초기값·외부 초기화용으로만 반영한다 (타이핑 중 innerHTML을 되돌리면 커서가 튄다)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (!value && el.innerHTML !== '') { el.innerHTML = ''; setEmpty(true) }
    else if (value && el.innerHTML === '') { el.innerHTML = value; setEmpty(false) }
  }, [value])

  // 캡처만 붙여넣은 경우 글자는 없어도 내용이 있는 것 — 이미지도 같이 본다
  const sync = () => {
    const el = ref.current
    const blank = !el.textContent.trim() && !el.querySelector('img')
    setEmpty(blank)
    onChange(blank ? '' : el.innerHTML)
  }

  // Enter 를 <div> 대신 <p> 로 넣게 한다. 둘 다 서버가 허용하지만 문단이라야
  // 줄 간격이 맞고, 옛 브라우저에서 <div> 가 중첩되는 것도 피한다.
  useEffect(() => {
    try { document.execCommand('defaultParagraphSeparator', false, 'p') } catch { /* 무시 */ }
  }, [])

  const paste = async (e) => {
    const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith('image/'))
    if (!item) return                       // 글자 붙여넣기는 브라우저 기본 동작 그대로
    e.preventDefault()
    const file = item.getAsFile()
    if (!file) return
    try {
      const dataUrl = await shrinkImage(file)
      let src = dataUrl
      if (uploadImage) {
        // 올리다 실패하면 base64로라도 남긴다. 방금 붙여넣은 캡처를 말없이 버리는 것보다는
        // 무거운 본문이 낫다 - 다시 캡처하라고 하면 그 자리에서 편집을 그만둔다.
        try { src = await uploadImage(dataUrl) } catch { src = dataUrl }
      }
      ref.current.focus()
      document.execCommand('insertHTML', false, `<img src="${src}" alt="붙여넣은 이미지">`)
      sync()
    } catch {
      window.alert('이미지를 붙여넣지 못했습니다. 증빙 첨부로 올려주세요.')
    }
  }

  const run = (t) => {
    ref.current.focus()
    if (t.cmd === 'link') {
      const url = window.prompt('링크 주소를 입력하세요', 'https://')
      if (url) document.execCommand('createLink', false, url)
    } else {
      document.execCommand(t.cmd, false, t.arg)
    }
    sync()
  }

  return (
    <div className="rte">
      <div className="rte-toolbar">
        {TOOLS.map((t, i) => (t.sep
          ? <span key={i} className="rte-sep" />
          : (
            <button key={i} type="button" title={t.title} style={t.style}
              onMouseDown={(e) => e.preventDefault()} onClick={() => run(t)}>
              {t.label}
            </button>
          )))}
      </div>
      <div
        ref={ref}
        className={`rte-body${empty ? ' empty' : ''}`}
        style={{ minHeight }}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={sync}
        onBlur={sync}
        onPaste={paste}
      />
    </div>
  )
}
